import { asyncTryAndReturn } from '../../common/catcher'
import { performanceAsyncWrapper } from '../../common/performance-decorator'
import { isFulfilled } from '../../http-utils/utils'
import { setContextLocalStoragePbxInfo, setContextLocalStorageAccountKey, getPbxData } from '../../services/context'
import type { ContextApi, PbxData } from '../../services/context/models'
import { AccountContext, PbxContext } from '../../services/context/models'
import { getProductTrialInfo } from '../../services/product'

/**
 * Sets up the context API with the given email and external user key.
 * @param email The email to use for fetching context data.
 * @param externalUserKey The external user key to use for fetching context data.
 * @returns A Promise that resolves to the context API object.
 */
export const setupContextApi = async (
  email: string | undefined,
  externalUserKey: string | undefined,
): Promise<ContextApi> =>
  await performanceAsyncWrapper('shell_contextApiSetup', async () => {
    let context: ContextApi = {}
    if (externalUserKey) {
      const [pbxData, productTrial] = await fetchContextData(email, externalUserKey)

      storePbxDataInLocalStorage(pbxData, externalUserKey)

      context = {
        account: new AccountContext(pbxData?.accountKey),
        pbx: new PbxContext(pbxData?.pbxId, pbxData?.pbxUserId, pbxData?.pbxFlags),
        productTrial,
      }
    }

    return context
  })()

const fetchContextData = async (email: string | undefined, externalUserKey: string) => {
  const contextPromises = []
  const getProductTrialPromise = asyncTryAndReturn({ func: () => getProductTrialInfo(), defaultReturn: undefined })
  let getPbxDataPromise = Promise.resolve(undefined)

  if (email) {
    getPbxDataPromise = asyncTryAndReturn({ func: () => getPbxData(email, externalUserKey), defaultReturn: undefined })
  }

  contextPromises.push(getPbxDataPromise)
  contextPromises.push(getProductTrialPromise)

  const results = await Promise.allSettled(contextPromises)
  const [pbxData, productTrial] = results.map(result => {
    if (isFulfilled(result)) {
      return result.value
    }
    return
  })

  return [pbxData, productTrial]
}

const storePbxDataInLocalStorage = (pbxData: PbxData, externalUserKey: string) => {
  if (pbxData?.pbxId) {
    setContextLocalStoragePbxInfo({ externalUserKey, pbxId: pbxData?.pbxId, pbxUserId: pbxData?.pbxUserId })
  }
  if (pbxData?.accountKey) {
    setContextLocalStorageAccountKey(externalUserKey, pbxData.accountKey)
  }
}
